import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import PaymentModal from "../components/ExpensePaymentModal";
import EditInvoiceModal from "../components/EditExpenseModal";
import { useNavigate } from 'react-router-dom';
import DynamicNav from "../config/dynamicNav";
import {
  Typography, Box, Paper, Grid, Chip, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Skeleton, IconButton,
  Alert, Snackbar, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, useTheme,
} from '@mui/material';
import {
  Check, Warning, ErrorOutline, InsertDriveFile, FileDownload,
  Print, Payment, Edit, Close, OpenInNew,
} from '@mui/icons-material';
import '../index.css';
import TopBar from "../components/TopBar";
import SideBar from "../components/SideBar";

/* ─── Global font injection ───────────────────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap');
    body, * { font-family: 'Quicksand', sans-serif !important; }
  `}</style>
);

/* ─── Design tokens ───────────────────────────────────────────────────────── */
const T = {
  white: '#ffffff',
  bg: '#f8f9fc',
  surface: '#ffffff',
  border: '#e8ecf4',
  borderLight: '#f0f3fb',
  accent: '#5b4fcf',
  accentLight: '#ede9fd',
  accentMid: '#7c6fe0',
  text: '#1a1d2e',
  textSub: '#6b7280',
  textMuted: '#9ca3af',
  success: '#059669',
  successBg: '#ecfdf5',
  successBorder: '#a7f3d0',
  warning: '#d97706',
  warningBg: '#fffbeb',
  error: '#dc2626',
  errorBg: '#fef2f2',
  shadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 24px rgba(0,0,0,0.08)',
  radius: '14px',
};

const card = {
  background: T.surface,
  borderRadius: T.radius,
  border: `1px solid ${T.border}`,
  boxShadow: T.shadow,
};

const pillBtn = (variant = 'accent') => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: 'Quicksand, sans-serif',
  fontSize: '13px',
  px: 2.5,
  py: 1,
  transition: 'all 0.18s ease',
  ...(variant === 'accent' && {
    background: T.accent,
    color: '#fff',
    border: 'none',
    '&:hover': { background: T.accentMid, boxShadow: '0 4px 14px rgba(91,79,207,0.35)' },
    '&.Mui-disabled': { background: '#d1d5db', color: '#fff' },
  }),
  ...(variant === 'outline' && {
    background: T.white,
    color: T.accent,
    border: `1.5px solid ${T.accent}`,
    '&:hover': { background: T.accentLight },
  }),
  ...(variant === 'ghost' && {
    background: T.bg,
    color: T.textSub,
    border: `1px solid ${T.border}`,
    '&:hover': { background: T.border },
  }),
});

/* ─── Status chip ─────────────────────────────────────────────────────────── */
const InvoiceStatus = ({ status }) => {
  const map = {
    paid:    { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0', icon: <Check sx={{ fontSize: 14 }} />,          label: 'Paid' },
    pending: { bg: '#fffbeb', color: '#d97706', border: '#fcd34d', icon: <Warning sx={{ fontSize: 14 }} />,        label: 'Pending' },
    overdue: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', icon: <ErrorOutline sx={{ fontSize: 14 }} />,   label: 'Overdue' },
    draft:   { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1', icon: <InsertDriveFile sx={{ fontSize: 14 }} />, label: 'Draft' },
  };
  const c = map[(status || 'pending').toLowerCase()] || map.pending;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: '8px', px: 1.2, py: 0.4, fontSize: '12px', fontWeight: 700,
    }}>
      {c.icon}{c.label}
    </Box>
  );
};

/* ─── Formatters ──────────────────────────────────────────────────────────── */
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

/* ─── Stat card ───────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, accent }) => (
  <Box sx={{
    background: accent ? T.accentLight : T.bg,
    borderRadius: '12px',
    border: `1px solid ${accent ? '#d4cff7' : T.borderLight}`,
    px: 2, py: 1.5, flex: 1,
  }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: accent ? T.accent : T.text }}>
      {value}
    </Typography>
  </Box>
);

/* ─── Asset recognition confirm dialog ───────────────────────────────────── */
const AssetDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{
    sx: { borderRadius: '18px', border: `1px solid ${T.border}`, boxShadow: T.shadowMd }
  }}>
    <DialogTitle sx={{ pb: 1.5, borderBottom: `1px solid ${T.border}`, px: 3, pt: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '18px' }}>🏛</Typography>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.text, fontFamily: 'Quicksand, sans-serif' }}>
            Recognize as Asset
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ px: 3, py: 2.5 }}>
      <Typography sx={{ fontSize: '14px', color: T.textSub, fontFamily: 'Quicksand, sans-serif', lineHeight: 1.6 }}>
        This will recognize the selected expense item as a capital asset. Do you want to proceed?
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
      <Button onClick={onClose} sx={pillBtn('ghost')}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained" startIcon={<OpenInNew sx={{ fontSize: 16 }} />} sx={pillBtn('accent')}>
        Proceed & Recognize
      </Button>
    </DialogActions>
  </Dialog>
);

/* ─── Main component ──────────────────────────────────────────────────────── */
const ExpenseInvoicesLayout = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [studentInfo, setStudentInfo] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [latestInvoice, setInvoiceLatest] = useState(null);
  const [previousInvoices, setPreviousInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [invoicesData, setInvoicesData] = useState([]);
  const [getInvoiceItems, setInvoiceItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [invoiceInfo, setInvoiceInfo] = useState([]);
  const [paymentInputs, setPaymentInputs] = useState({});
  const [selectedId, setSelectedId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditOpen, setEditModalOpen] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);

  const token = localStorage.getItem('token');
  const getToken = () => localStorage.getItem('token');
  const token_ = getToken();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || config.API_URL + '/api';

  const handleMarkPaid = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);
  const handleEditModalClose = () => setEditModalOpen(false);

  const handleViewActions = (itemId) => {
    setSelectedId(itemId);
    setAssetDialogOpen(true);
  };

  const proceedToAsset = () => {
    setAssetDialogOpen(false);
    navigate(`/Assets_/${selectedId}`);
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  };

  const handleSubmitPayment = async (payload) => {
    try {
      const token = localStorage.getItem("token");
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');
      const response = await axios.post(`${config.API_URL}/api/saveExpensePayments`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json', 'X-XSRF-TOKEN': xsrfToken },
        withCredentials: true,
      });
      const data = response.data;
      window.location.assign(`/viewexpense/${payload.invoice_id}`);
      setNotification({ open: true, message: data.message || "Payment saved successfully.", severity: 'success' });
      return { success: true, message: data.message || "Payment saved successfully." };
    } catch (error) {
      return { success: false, message: error.message || "Network error." };
    }
  };

  const handleEditSubmitPayment = async (payload) => {
    try {
      const token = localStorage.getItem("token");
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');
      const response = await fetch(`${config.API_URL}/api/saveExpenseChanges`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json', 'X-XSRF-TOKEN': xsrfToken },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      if (!response.ok) {
        let message = "Failed to save expense.";
        try { const json = JSON.parse(text); message = json.message || message; } catch (_) { if (text) message = text; }
        return { success: false, message };
      }
      const data = text ? JSON.parse(text) : {};
      window.location.assign(`/viewexpense/${id}`);
      return { success: true, message: data.message || "Expense saved successfully." };
    } catch (error) {
      return { success: false, message: error.message || "Network error." };
    }
  };

  const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } });
  api.interceptors.request.use((cfg) => { const t = getToken(); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; }, (err) => Promise.reject(err));

  useEffect(() => {
    axios.get(config.API_URL + `/api/studentExpense/${id}`, { headers: { Authorization: `Bearer ${token_}` } })
      .then(r => setInvoiceInfo(r.data)).catch(() => {});

    axios.get(config.API_URL + `/api/latestExpenseV2/${id}`, { headers: { Authorization: `Bearer ${token_}` } })
      .then(r => {
        setInvoiceLatest({ id: r.data.id, customer_id: r.data.customer_id, invoiceNumber: r.data.invoiceNumber, invoiceIssueDate: r.data.invoiceIssueDate, invoiceDueDate: r.data.invoiceDueDate, invoiceAmount: r.data.invoiceAmount, status: r.data.status, raw: r.data.invoice });
        setInvoiceItems(r.data.invoice_items);
        setPreviousInvoices(r.data.otherInvoices);
        setAccounts(r.data.accounts);
      }).catch(() => {});
  }, [id]);

  const handleEditModal = async (item_id) => {
    setEditModalOpen(true);
    setLoading(true);
    try {
      const res = await api.get(config.API_URL + `/api/pullExpenseItem/${item_id}`);
      setInvoiceItems(res.data.invoice_items);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentInfo = useCallback(async () => {
    try { const res = await api.get(`/studentInvoiceInfo/${id}`); setStudentInfo(res.data); } catch (err) {}
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true); setError(false);
    try { const res = await api.get(`/student/${id}/invoices`); setInvoicesData(res.data.data); } catch (err) { setError(true); } finally { setLoading(false); }
  }, []);

  const processInvoicesData = useCallback((data) => {
    if (!data || data.length === 0) return;
    [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);

  useEffect(() => { if (invoicesData.length > 0) processInvoicesData(invoicesData); }, [sortConfig, invoicesData, processInvoicesData]);
  useEffect(() => { fetchStudentInfo(); fetchInvoices(); }, []);

  const handleDownloadInvoice = () => {
    setNotification({ open: true, message: 'Expense download started', severity: 'info' });
    window.open(`${config.API_URL}/downloadExpense/${id}/${token}`);
  };

  const handlePrintInvoice = () => {
    setNotification({ open: true, message: 'Preparing expense for printing', severity: 'info' });
    window.open(`${config.API_URL}/downloadExpense/${id}/${token}`);
  };

  const handleCloseNotification = () => setNotification({ ...notification, open: false });

  const expenseTotals = useMemo(() => ({
    total:   getInvoiceItems.reduce((s, i) => s + (parseFloat(i.total)   || 0), 0),
    paid:    getInvoiceItems.reduce((s, i) => s + (parseFloat(i.paid)    || 0), 0),
    balance: getInvoiceItems.reduce((s, i) => s + (parseFloat(i.balance) || 0), 0),
  }), [getInvoiceItems]);

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="d-flex flex-column" id="kt_app_root">
        <GlobalStyle />
        <TopBar />
        <div className="d-flex flex-grow-1">
          <SideBar />
          <Box sx={{ flex: 1, p: 4, background: T.bg }}>
            <Skeleton variant="rectangular" height={56} width="40%" sx={{ mb: 3, borderRadius: T.radius }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: '12px', flex: 1 }} />)}
            </Box>
            <Skeleton variant="rectangular" height={420} sx={{ borderRadius: T.radius }} />
          </Box>
        </div>
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="d-flex flex-column" id="kt_app_root">
        <GlobalStyle />
        <TopBar />
        <div className="d-flex flex-grow-1">
          <SideBar />
          <Box sx={{ flex: 1, p: 4, background: T.bg }}>
            <Alert severity="error" sx={{ borderRadius: T.radius }}>
              Error loading expenses. Please try again later or contact support.
            </Alert>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" id="kt_app_root">
      <GlobalStyle />
      <TopBar />
      <div className="d-flex flex-grow-1">
        <SideBar />

        <Box sx={{ flex: 1, background: T.bg, minHeight: '100vh', p: { xs: 2, md: 3.5 } }}>
          <DynamicNav />

          {/* ── Page header ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, mt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: T.text, lineHeight: 1.2 }}>
                Expenses Management
              </Typography>
              <Typography sx={{ fontSize: '13px', color: T.textSub, fontWeight: 500, mt: 0.3 }}>
                Expense #{latestInvoice?.invoiceNumber || '—'}
              </Typography>
            </Box>

            {/* ── Action buttons ── */}
            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
              <Tooltip title="Download Expense">
                <Button startIcon={<FileDownload sx={{ fontSize: 16 }} />} onClick={handleDownloadInvoice} sx={pillBtn('ghost')}>
                  Download
                </Button>
              </Tooltip>
              <Tooltip title="Print Expense">
                <Button startIcon={<Print sx={{ fontSize: 16 }} />} onClick={handlePrintInvoice} sx={pillBtn('ghost')}>
                  Print
                </Button>
              </Tooltip>
              <Button
                startIcon={<Edit sx={{ fontSize: 16 }} />}
                onClick={() => handleEditModal(latestInvoice?.id)}
                sx={pillBtn('outline')}
              >
                Edit
              </Button>
              <Button
                startIcon={<Payment sx={{ fontSize: 16 }} />}
                disabled={latestInvoice?.status?.toLowerCase() === 'paid'}
                onClick={handleMarkPaid}
                sx={pillBtn('accent')}
              >
                Add Payment
              </Button>
            </Box>
          </Box>

          {/* ── Stat cards ── */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <StatCard label="Expense Total" value={formatCurrency(expenseTotals.total)} />
            <StatCard label="Total Paid"    value={formatCurrency(expenseTotals.paid)} />
            <StatCard label="Balance Due"   value={formatCurrency(expenseTotals.balance)} accent />
          </Box>

          {/* ── Expense card ── */}
          {latestInvoice && (
            <Paper elevation={0} sx={{ ...card, p: 0, overflow: 'hidden', mb: 3 }}>

              {/* Card header bar */}
              <Box sx={{
                px: 3.5, py: 2.5, borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: T.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '18px' }}>📋</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.text }}>
                      Expense #{latestInvoice.invoiceNumber}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: T.textSub }}>
                      Issued {formatDate(latestInvoice.invoiceIssueDate)}
                    </Typography>
                  </Box>
                </Box>
                <InvoiceStatus status={latestInvoice.status || 'pending'} />
              </Box>

              {/* Meta grid */}
              <Box sx={{ px: 3.5, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {[
                      ['Expense Number', latestInvoice.invoiceNumber],
                      ['Issue Date',     formatDate(latestInvoice.invoiceIssueDate)],
                      ['Due Date',       formatDate(latestInvoice.invoiceDueDate)],
                    ].map(([label, val]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${T.borderLight}` }}>
                        <Typography sx={{ fontSize: '13px', color: T.textSub, fontWeight: 500 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: T.text }}>{val}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {[
                      ['Expense Amount', formatCurrency(latestInvoice.invoiceAmount)],
                      ['Description',    latestInvoice.note || '—'],
                    ].map(([label, val]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${T.borderLight}` }}>
                        <Typography sx={{ fontSize: '13px', color: T.textSub, fontWeight: 500 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: T.text }}>{val}</Typography>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </Box>

              {/* Expense items table */}
              <Box sx={{ px: 3.5, pb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: T.text, mb: 1.5 }}>
                  Expense Items
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: T.bg }}>
                      {['Description', 'Quantity', 'Total', 'Paid', 'Balance'].map(h => (
                        <TableCell key={h} align={h === 'Description' ? 'left' : 'right'} sx={{
                          fontWeight: 700, fontSize: '12px', color: T.textSub,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          py: 1.2, px: 2, borderBottom: `1px solid ${T.border}`,
                        }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getInvoiceItems.map((item, idx) => (
                      <TableRow key={idx} sx={{
                        background: idx % 2 === 0 ? T.white : T.bg,
                        '&:hover': { background: T.accentLight },
                        transition: 'background 0.1s',
                      }}>
                        <TableCell sx={{ py: 1.5, px: 2 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: T.text }}>
                            {item.description}
                          </Typography>
                          <Box
                            onClick={() => handleViewActions(item.id)}
                            sx={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px', mt: 0.4,
                              color: T.accent, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                              textDecoration: 'underline', textUnderlineOffset: '2px',
                              '&:hover': { color: T.accentMid },
                            }}
                          >
                            <OpenInNew sx={{ fontSize: 12 }} /> View Actions
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', color: T.textSub, py: 1.5, px: 2 }}>{item.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: T.text, py: 1.5, px: 2 }}>{formatCurrency(item.total)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, color: T.success, py: 1.5, px: 2 }}>{formatCurrency(item.paid)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, color: item.balance > 0 ? T.error : T.textSub, py: 1.5, px: 2 }}>{formatCurrency(item.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>

                  {/* Totals row */}
                  <TableBody>
                    <TableRow sx={{ background: T.bg }}>
                      <TableCell colSpan={2} sx={{ borderTop: `2px solid ${T.border}`, py: 1.5, px: 2 }} />
                      {[expenseTotals.total, expenseTotals.paid, expenseTotals.balance].map((val, i) => (
                        <TableCell key={i} align="right" sx={{ borderTop: `2px solid ${T.border}`, py: 1.5, px: 2 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: T.accent }}>
                            {formatCurrency(val)}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ height: 24 }} />
            </Paper>
          )}

          {/* ── Modals ── */}
          <PaymentModal
            open={modalOpen}
            onClose={handleModalClose}
            invoice={latestInvoice?.id}
            invoiceItems={getInvoiceItems}
            accounts={accounts}
            onSubmit={handleSubmitPayment}
          />
          <EditInvoiceModal
            open={modalEditOpen}
            onClose={handleEditModalClose}
            invoice={latestInvoice}
            invoiceItems={getInvoiceItems}
            onSubmit={handleEditSubmitPayment}
          />
          <AssetDialog
            open={assetDialogOpen}
            onClose={() => setAssetDialogOpen(false)}
            onConfirm={proceedToAsset}
          />

          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseNotification} severity={notification.severity}
              sx={{ borderRadius: '12px', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
              {notification.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default ExpenseInvoicesLayout;